import '@vaadin/time-picker';
import { css, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { TmFormControl } from './forms';

const tagName = 'tm-time-picker';

@customElement(tagName)
export class TmTimePicker extends TmFormControl<string> {
    @property({ type: String })
    public label?: string;

    @property({ type: Boolean })
    public disabled?: boolean;

    @property({ type: String })
    public minTime?: string;

    @query('vaadin-time-picker')
    private timePicker!: LitElement & {
        i18n: {
            formatTime: (time: { hours: number; minutes: number }) => string;
            parseTime: (time: string) => { hours: number; minutes: number };
        };
        min: string;
        value: string;
    };

    public updated(changedProperties: Map<string, unknown>) {
        super.updated(changedProperties);
        if (changedProperties.has('minTime')) {
            const minTime24Hour = this._to24HourFormat(this.minTime);
            if (this.timePicker) {
                this.timePicker.min = minTime24Hour || '';
            }

            // Revalidate current value against new min time
            const currentValue = this._to24HourFormat(this.initial as string);
            if (
                minTime24Hour &&
                currentValue &&
                this._isTimeLessThan(currentValue, minTime24Hour)
            ) {
                this.changeValue('');
            }
        }
    }

    public firstUpdated() {
        if (this.timePicker) {
            // Set initial min value
            const minTime24Hour = this._to24HourFormat(this.minTime);
            if (minTime24Hour) {
                this.timePicker.min = minTime24Hour;
            }

            this.timePicker.i18n = {
                ...this.timePicker.i18n,
                formatTime: time => {
                    if (time) {
                        const hours = time.hours % 12 || 12;
                        const minutes = time.minutes.toString().padStart(2, '0');
                        const ampm = time.hours < 12 ? 'AM' : 'PM';
                        return `${hours}:${minutes} ${ampm}`;
                    }
                    return '';
                },
                parseTime: timeStr => {
                    if (timeStr) {
                        const [time, ampm] = timeStr.split(' ');
                        const splitTime = time.split(':').map(Number);
                        let hours = splitTime[0];
                        const minutes = splitTime[1];
                        if (ampm === 'PM' && hours < 12) {
                            hours += 12;
                        }
                        if (ampm === 'AM' && hours === 12) {
                            hours = 0;
                        }
                        return { hours, minutes };
                    }
                    return { hours: 0, minutes: 0 };
                },
            };
        }
    }

    public render() {
        const twentyFourHourValue = this._to24HourFormat(this.initial as string);
        const minTime24Hour = this._to24HourFormat(this.minTime);

        // Set value to minTime if current value is less than minTime
        let displayValue = twentyFourHourValue;
        if (
            minTime24Hour &&
            twentyFourHourValue &&
            this._isTimeLessThan(twentyFourHourValue, minTime24Hour)
        ) {
            displayValue = '';
        }

        return html`<vaadin-time-picker
            @value-changed=${this._handleChange}
            .label=${this.label}
            .value=${displayValue}
            .disabled=${this.disabled}
            .step=${1800}
            .min=${minTime24Hour}
        ></vaadin-time-picker>`;
    }

    protected validate() {
        return true;
    }

    private _handleChange(e: CustomEvent<{ value: string }>) {
        this.changeValue(e.detail.value);
    }

    private _to24HourFormat(value: string | undefined): string | undefined {
        if (!value) {
            return undefined;
        }

        const ampmMatch = value.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);

        if (ampmMatch) {
            let hours = parseInt(ampmMatch[1], 10);
            const minutes = parseInt(ampmMatch[2], 10);
            const ampm = ampmMatch[3].toUpperCase();

            if (ampm === 'PM' && hours < 12) {
                hours += 12;
            } else if (ampm === 'AM' && hours === 12) {
                // Midnight case
                hours = 0;
            }
            const hh = String(hours).padStart(2, '0');
            const mm = String(minutes).padStart(2, '0');

            return `${hh}:${mm}`;
        }
        return value;
    }

    private _to12HourFormat(value: string): string {
        const [hours, minutes] = value.split(':').map(Number);
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const hh = hours % 12 || 12;
        return `${hh}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    }

    private _isTimeLessThan(time1: string, time2: string): boolean {
        const [hours1, minutes1] = time1.split(':').map(Number);
        const [hours2, minutes2] = time2.split(':').map(Number);

        if (hours1 < hours2) {
            return true;
        }
        if (hours1 === hours2 && minutes1 < minutes2) {
            return true;
        }
        return false;
    }

    public get styles() {
        return css`
            :host {
                display: block;
            }
            vaadin-time-picker input {
                background-color: red;
            }
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmTimePicker;
    }
}
